// Spec 003-previous-owners-list-ui — pure presentational component.
// Spec 006-ownership-privacy-stamping — rewrite to consume OwnerRecord[].
//
// PRIVACY CONTRACT: a current owner who reads this section MUST see at most
// the timeframe of each prior stewardship and (when recorded) a coarse-grained
// location. No UID, no name, no email, no notes. The component does NOT call
// any UID-to-name resolver — that seam (spec 005's `useUsers`) was deleted in
// spec 006 because its very existence enabled the leak this contract closes.
//
// See `memory/project_ownership_privacy_model.md` for the full rule. If a
// future change adds an identifying field to OwnerRecord, the rendering here
// MUST NOT surface it without an explicit privacy-impact review.
//
// Two render branches:
//   - non-empty array: <ul>/<li> timeline rail — a hairline + dots with a
//     "from → to · location?" body per record (heritage-ledger aesthetic).
//   - empty array: explicit "No previous owners on record" copy. The section
//     stays visible via the Collapsible header — silent absence violates the
//     offline-first explicit-data principle (constitution I).
//
// The section is collapsed by default; the Collapsible provides the card,
// heading, and region landmark. No firebase/* imports. No hooks. Pure read of props.

import { Collapsible } from '@/components/Collapsible';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import type { OwnerRecord } from '@/types/domain';
import { Users } from 'lucide-react';

export interface PreviousOwnersListProps {
  previousOwners: readonly OwnerRecord[];
}

// Dimensions for the timeline rail — kept as constants so the grid column
// width and the dot size stay in sync without magic numbers scattered below.
const DOT_SIZE = 9; // px — circle diameter
const RAIL_COL_WIDTH = 20; // px — left grid column; centres the dot + rail

export function PreviousOwnersList({ previousOwners }: PreviousOwnersListProps) {
  return (
    <Collapsible
      title="Previous Owners"
      count={previousOwners.length}
      id="prev-owners"
      leadingIcon={<Users size={14} />}
    >
      {previousOwners.length === 0 ? (
        <p
          style={{
            color: T.textMuted,
            fontSize: 13,
            margin: 0,
          }}
        >
          No previous owners on record.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {previousOwners.map((record, idx) => {
            const isLast = idx === previousOwners.length - 1;
            const timeframe = `${record.from} → ${record.to ?? 'present'}`;
            // Derive a stable key from the ownership timeframe. Append index
            // only as a tiebreaker for the unusual case of duplicate records
            // with the same from/to dates (e.g. data-entry corrections).
            const stableKey = `${record.from}-${record.to ?? 'present'}-${idx}`;

            return (
              <li
                key={stableKey}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `${RAIL_COL_WIDTH}px 1fr`,
                  // Comfortable row spacing; no 44px touch-target needed
                  // (non-interactive element).
                  paddingBottom: isLast ? 0 : SPACE.sm,
                }}
              >
                {/* ── Left column: dot + connecting rail segment ── */}
                <div
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    // Top-align so the dot sits at cap-height of the first
                    // line of text to its right.
                    paddingTop: 3,
                  }}
                >
                  {/* Dot */}
                  <div
                    style={{
                      width: DOT_SIZE,
                      height: DOT_SIZE,
                      borderRadius: '50%',
                      background: T.card,
                      border: `2px solid ${T.border}`,
                      flexShrink: 0,
                    }}
                  />
                  {/* Rail line — omitted on the last row */}
                  {!isLast && (
                    <div
                      style={{
                        flex: 1,
                        width: 1,
                        background: T.border,
                        marginTop: 3,
                      }}
                    />
                  )}
                </div>

                {/* ── Right column: timeframe + optional location ── */}
                <div
                  style={{
                    paddingLeft: SPACE.xs,
                    paddingTop: 2,
                  }}
                >
                  <span
                    style={{
                      fontFamily: F.mono,
                      fontSize: 13,
                      color: T.text,
                      wordBreak: 'break-all',
                    }}
                  >
                    {timeframe}
                  </span>
                  {record.location && (
                    <span
                      style={{
                        fontFamily: F.mono,
                        fontSize: 13,
                        color: T.textMuted,
                        wordBreak: 'break-all',
                      }}
                    >
                      {' · '}
                      {record.location}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Collapsible>
  );
}
